/**
 * Realtime hook — opens a single shared WebSocket to /api/ws and dispatches
 * tenant-wide events to subscribed React components.
 *
 * Usage:
 *   useRealtime(['feed.created', 'dashboard.updated'], () => loadData());
 *
 * Auto-reconnects with exponential backoff. No-op gracefully if the socket
 * cannot connect (e.g. older deploys without WS support).
 */

import { useEffect, useRef, useState } from 'react';
import { getApiUrl } from './api';

let sharedSocket = null;
let reconnectAttempts = 0;
let pingInterval = null;
let reconnectTimer = null;
let disconnectTimer = null;
// V6.0.100 — After N consecutive failures we give up silently. Some preview /
// staging environments don't forward WS upgrades through the ingress; keeping
// the retry loop alive just floods the console with red errors on every
// dashboard load. Real deploys reset this counter on first successful open.
const MAX_RECONNECT_ATTEMPTS = 3;
let _wsGaveUp = false;
const listeners = new Set(); // {events: Set<string>|null, callback}
const statusListeners = new Set(); // {callback}
let _status = 'connecting'; // 'connecting' | 'online' | 'offline'

const setStatus = (s) => {
  if (_status === s) return;
  _status = s;
  statusListeners.forEach((l) => { try { l.callback(s); } catch { /* noop */ } });
};

// The browser's own view of the network. A dead WebSocket (e.g. a preview env
// that doesn't forward WS upgrades) must NOT be reported as "offline" — the app
// still works over HTTP. Only a true network loss shows the "hors ligne" banner.
const netOffline = () => (typeof navigator !== 'undefined' && navigator.onLine === false);

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => setStatus('offline'));
  window.addEventListener('online', () => {
    _wsGaveUp = false;
    reconnectAttempts = 0;
    if (listeners.size || statusListeners.size) connect();
    else setStatus('online');
  });
}

const wsUrl = () => {
  const base = getApiUrl(); // e.g. https://360-resto.com/api  OR  /api
  let httpUrl;
  if (base.startsWith('/')) {
    // Relative — anchor on current origin
    httpUrl = `${window.location.origin}${base}`;
  } else {
    httpUrl = base;
  }
  // Convert http(s):// → ws(s)://
  return httpUrl.replace(/^http/, 'ws') + '/ws';
};

const connect = () => {
  clearTimeout(disconnectTimer);
  disconnectTimer = null;
  if (_wsGaveUp) return; // V6.0.100 — silent no-op after repeated failures
  if (sharedSocket && (sharedSocket.readyState === 0 || sharedSocket.readyState === 1)) {
    return; // Already connecting/connected
  }
  setStatus('connecting');
  try {
    sharedSocket = new WebSocket(wsUrl());
  } catch {
    setStatus(netOffline() ? 'offline' : 'connecting');
    return;
  }
  const socket = sharedSocket;

  sharedSocket.onopen = () => {
    if (sharedSocket !== socket) return;
    reconnectAttempts = 0;
    _wsGaveUp = false;
    setStatus('online');
    if (pingInterval) clearInterval(pingInterval);
    pingInterval = setInterval(() => {
      try {
        if (socket.readyState === 1) socket.send('ping');
      } catch { /* noop */ }
    }, 25000);
  };

  sharedSocket.onmessage = (ev) => {
    if (sharedSocket !== socket) return;
    if (ev.data === 'pong') return;
    let payload;
    try { payload = JSON.parse(ev.data); } catch { return; }
    const event = payload.event;
    if (!event) return;
    listeners.forEach((l) => {
      if (!l.events || l.events.has(event)) {
        try { l.callback(payload); } catch { /* noop */ }
      }
    });
  };

  sharedSocket.onclose = () => {
    if (sharedSocket !== socket) return;
    sharedSocket = null;
    if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
    setStatus(netOffline() ? 'offline' : 'connecting');
    if (listeners.size === 0 && statusListeners.size === 0) return;
    reconnectAttempts += 1;
    if (reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
      _wsGaveUp = true; // Stop retrying — env doesn't support WS upgrade
      // App still works over HTTP: don't leave a false "offline" state behind.
      if (!netOffline()) setStatus('online');
      return;
    }
    const delay = Math.min(15000, 1000 * Math.pow(2, reconnectAttempts));
    clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(connect, delay);
  };

  sharedSocket.onerror = () => {
    if (sharedSocket !== socket) return;
    try { socket.close(); } catch { /* noop */ }
  };
};

const disconnectWhenUnused = () => {
  if (listeners.size || statusListeners.size) return;
  clearTimeout(disconnectTimer);
  // Route transitions/StrictMode briefly unmount all subscribers; keep the socket warm.
  disconnectTimer = setTimeout(() => {
    if (listeners.size || statusListeners.size) return;
    clearTimeout(reconnectTimer);
    clearInterval(pingInterval);
    pingInterval = null;
    const socket = sharedSocket;
    sharedSocket = null;
    if (!socket) return;
    socket.onclose = null;
    socket.onerror = null;
    socket.onmessage = null;
    socket.onopen = null;
    if (socket.readyState === WebSocket.CONNECTING) socket.addEventListener('open', () => socket.close(), {once:true});
    else socket.close();
  }, 300);
};

export const useRealtime = (events, callback) => {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    const l = {
      events: events ? new Set(events) : null,
      callback: (payload) => cbRef.current && cbRef.current(payload),
    };
    listeners.add(l);
    connect();
    return () => {
      listeners.delete(l);
      disconnectWhenUnused();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(events)]);
};

/** Subscribe to connection status changes. Returns 'connecting' | 'online' | 'offline'. */
export const useRealtimeStatus = () => {
  const [status, setS] = useState(_status);
  useEffect(() => {
    const l = { callback: (s) => setS(s) };
    statusListeners.add(l);
    connect();
    return () => {
      statusListeners.delete(l);
      disconnectWhenUnused();
    };
  }, []);
  return status;
};
